import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Badge, Button, EmptyState, QueryStateBoundary, Select } from "@/components/ui";
import { useEvents } from "@/api/hooks/useEvents";
import { useContact } from "@/api/hooks/useContacts";
import {
  EVENT_STATUS_BADGE_VARIANT,
  EVENT_STATUS_LABELS,
  type Event,
  type EventStatus,
} from "@/api/types/event";
import { formatShortDate, nextOpenTask } from "./eventDate";

type StatusFilter = EventStatus | "all";

const STATUS_OPTIONS: EventStatus[] = [
  "booked",
  "menu_confirmed",
  "prepped",
  "delivered",
  "closed",
  "canceled",
];

export function EventsPage() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useEvents({
    status: statusFilter === "all" ? undefined : statusFilter,
    from: from || undefined,
    to: to || undefined,
  });

  const events = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data]);

  return (
    <div className="p-6 sm:p-8">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="font-display text-2xl font-bold text-text-primary">Events</h1>
        <Button variant="secondary" onClick={() => navigate("/events/calendar")}>
          Calendar view
        </Button>
      </div>

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="min-w-[10rem]">
          <label className="mb-1 block text-xs font-medium text-text-secondary" htmlFor="event-status-filter">
            Status
          </label>
          <Select
            id="event-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          >
            <option value="all">All statuses</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {EVENT_STATUS_LABELS[s]}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-text-secondary" htmlFor="event-from">
            From
          </label>
          <input
            id="event-from"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="h-10 rounded-md border border-border bg-bg-card px-3 text-sm"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-text-secondary" htmlFor="event-to">
            To
          </label>
          <input
            id="event-to"
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="h-10 rounded-md border border-border bg-bg-card px-3 text-sm"
          />
        </div>
      </div>

      <QueryStateBoundary isLoading={isLoading} error={error} onRetry={() => void refetch()}>
        {events.length === 0 ? (
          <EmptyState
            icon="🍾"
            title="No events yet"
            description="Events are created automatically when a quote is accepted."
            action={{ label: "Go to quotes", to: "/quotes" }}
          />
        ) : (
          <div className="overflow-x-auto rounded-lg border border-border bg-bg-card">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-border bg-bg-hover/50 text-xs uppercase tracking-wide text-text-muted">
                <tr>
                  <th className="px-4 py-2 font-medium">Date</th>
                  <th className="px-4 py-2 font-medium">Contact / venue</th>
                  <th className="px-4 py-2 font-medium">Guests</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Next task</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {events.map((event) => (
                  <EventRow key={event.id} event={event} onOpen={() => navigate(`/events/${event.id}`)} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </QueryStateBoundary>

      {hasNextPage && (
        <div className="mt-4 flex justify-center">
          <Button variant="secondary" onClick={() => void fetchNextPage()} disabled={isFetchingNextPage}>
            {isFetchingNextPage ? "Loading..." : "Load more"}
          </Button>
        </div>
      )}

      <p className="mt-6 text-xs text-text-muted">
        Prefer the calendar?{" "}
        <Link to="/events/calendar" className="font-medium text-primary hover:underline">
          Switch to calendar view
        </Link>
        .
      </p>
    </div>
  );
}

interface EventRowProps {
  event: Event;
  onOpen: () => void;
}

function EventRow({ event, onOpen }: EventRowProps) {
  const { data: contact } = useContact(event.contact_id);
  const next = nextOpenTask(event.tasks);

  return (
    <tr
      data-event-id={event.id}
      className="cursor-pointer hover:bg-bg-hover"
      onClick={onOpen}
    >
      <td className="px-4 py-3 font-medium text-text-primary">{formatShortDate(event.event_date)}</td>
      <td className="px-4 py-3 text-text-primary">
        {contact?.name ?? "—"}
        {event.venue_name ? <span className="text-text-muted"> — {event.venue_name}</span> : null}
      </td>
      <td className="px-4 py-3 text-text-secondary">
        {event.guest_count}
        {event.na_guest_count ? ` (${event.na_guest_count} NA)` : ""}
      </td>
      <td className="px-4 py-3">
        <Badge variant={EVENT_STATUS_BADGE_VARIANT[event.status]}>
          {EVENT_STATUS_LABELS[event.status]}
        </Badge>
      </td>
      <td className="px-4 py-3">
        {next ? (
          <Badge variant="secondary" size="sm">
            {next.title}
            {next.due_date ? ` — ${formatShortDate(next.due_date)}` : ""}
          </Badge>
        ) : (
          <span className="text-xs text-text-muted">—</span>
        )}
      </td>
    </tr>
  );
}
